import { gql } from '@apollo/client';
import { Form, Input } from 'antd';
import React, { memo, useState, useEffect, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { InlineTooltip } from '@xbcb/feedback-components';
import { FormItem } from '@xbcb/form-item-components';
import { show } from '@xbcb/ui-utils';
import LineItemManufacturerInfo from 'components/LineItemManufacturerInfo';
import LineItemTariffs from 'components/LineItemTariffs';
import PendingUpdatesHighlight from 'components/PendingUpdatesHighlight';
import { useWorkOrderTaskConfiguration, useCurrentUser } from 'libs/hooks';
import { isReadOnly, shouldRequire } from 'libs/readWriteControl';
import type { RemoveAdditionalTariff } from 'libs/tariffs';
import tooltips from 'libs/tooltips';
import { CommercialInvoiceBaseProps } from 'types';
import { StyledContainer } from './styles';
import AIDrawer from 'components/AIDrawer';
import AIDrawerAlertMessageBox from 'components/AIDrawerAlertMessageBox';
import { isProject42Enabled } from 'featureFlags';
import { getClassificationService } from 'libs/p42/classification/factory';
import { UiStage } from '@xbcb/ui-types';
import { isRealHts } from '@xbcb/entry-utils/dist/lib/htsUtil';

export const lineItemInfoFragment = gql`
  fragment lineItemInfoFragment on UsConsumptionEntryLine {
    description
    poNumber
  }
`;

export interface LineInfoTabProps extends CommercialInvoiceBaseProps {
  index?: number;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  removeAdditionalTariff: RemoveAdditionalTariff;
}

export const LineInfoTab: React.FC<LineInfoTabProps> = ({
  fullNamePath,
  localNamePath,
  readOnly,
  disabled,
  required,
  workOrderType,
  removeAdditionalTariff,
}) => {
  const { readWriteControlsConfig } = useWorkOrderTaskConfiguration();
  const theme = useTheme();
  const form = Form.useFormInstance();
  const poNumberKey = [...localNamePath, 'poNumber'];
  const isWorkOrderType = !!workOrderType;

  const currentUser = useCurrentUser();
  const shipperId = currentUser.account?.id;
  const isP42Enabled = isProject42Enabled({ shipperId });
  const [showAIDrawer, setShowAIDrawer] = useState(false);
  const [showMismatchAlert, setShowMismatchAlert] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [validationResult, setValidationResult] = useState<any>(null);

  const htsNumbers = Form.useWatch([...fullNamePath, 'tariffs']);
  const productDescription = Form.useWatch(
    [...fullNamePath, 'description'],
    form,
  );
  const manufacturerName = Form.useWatch(
    [...fullNamePath, 'manufacturer', 'name'],
    form,
  );
  const countryOfOrigin = Form.useWatch(
    [...fullNamePath, 'origin', 'countryCode'],
    form,
  );

  // Memoize the real HTS entry to prevent unnecessary re-renders
  const realHtsEntry = useMemo(() => {
    return htsNumbers?.find((num: any) => isRealHts(num?.htsNumber));
  }, [htsNumbers]);

  // Memoize the validation inputs to prevent unnecessary re-renders
  const validationInputs = useMemo(
    () => ({
      htsNumber: realHtsEntry?.htsNumber,
      productDescription,
      manufacturerName,
      countryOfOrigin,
    }),
    [
      realHtsEntry?.htsNumber,
      productDescription,
      manufacturerName,
      countryOfOrigin,
    ],
  );

  useEffect(() => {
    if (!isP42Enabled) return;

    const service = getClassificationService(UiStage.GAMMA, shipperId);
    const validationResult = service.validateClassification(validationInputs);
    setValidationResult(validationResult);

    if (
      validationResult?.status === 'ERROR' &&
      validationResult?.errorCode === 'NOT_ENOUGH_INFORMATION'
    ) {
      setShowAIDrawer(true);
      setShowMismatchAlert(false);
      setShowSuccessMessage(false);
    } else if (validationResult?.errorCode === 'HTS_MISMATCH') {
      setShowMismatchAlert(true);
      setShowAIDrawer(false);
    } else {
      setShowAIDrawer(false);
      setShowMismatchAlert(false);
    }
  }, [isP42Enabled, shipperId, validationInputs]);

  return (
    <div data-testid={'line-info-tab'}>
      {!isP42Enabled && (
        <LineItemTariffs
          localNamePath={localNamePath}
          fullNamePath={fullNamePath}
          readOnly={readOnly}
          disabled={disabled}
          required={required}
          workOrderType={workOrderType}
          removeAdditionalTariff={removeAdditionalTariff}
        />
      )}

      <StyledContainer>
        <PendingUpdatesHighlight
          form={form}
          fullNamePath={[...fullNamePath, 'description']}
        >
          <FormItem
            name={[...localNamePath, 'description']}
            rules={[{ required: required, message: ' ' }]}
            label="CBP Description"
            $itemSize={theme.size.medium}
            $readOnly={
              readOnly ||
              isReadOnly(
                'invoices.products.lines.description',
                readWriteControlsConfig,
              )
            }
            required={shouldRequire(
              'invoices.products.lines.description',
              readWriteControlsConfig,
              required,
            )}
            debounce
          >
            <Input.TextArea autoSize disabled={disabled} maxLength={20000} />
          </FormItem>
        </PendingUpdatesHighlight>
        {!readOnly ? (
          <InlineTooltip title={tooltips.description} spaceTop spaceRight />
        ) : null}
        {isWorkOrderType &&
        show({
          readOnly,
          form,
          field: poNumberKey,
        }) ? (
          <FormItem
            $itemSize={theme.size.tiny_short}
            name={poNumberKey}
            label="PO Number"
            $readOnly={
              readOnly ||
              isReadOnly('invoices.lines.poNumber', readWriteControlsConfig)
            }
            required={shouldRequire(
              'invoices.lines.poNumber',
              readWriteControlsConfig,
              required,
            )}
            debounce
            getValueFromEvent={(e: React.ChangeEvent<HTMLInputElement>) => {
              const val = e.target.value;
              return val?.toUpperCase();
            }}
          >
            <Input
              disabled={disabled}
              data-testid={'po-input'}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.preventDefault();
              }}
            />
          </FormItem>
        ) : null}
      </StyledContainer>
      <LineItemManufacturerInfo
        workOrderType={workOrderType}
        localNamePath={localNamePath}
        fullNamePath={fullNamePath}
        readOnly={readOnly}
        disabled={disabled}
        required={required}
      />

      {isP42Enabled && (
        <LineItemTariffs
          localNamePath={localNamePath}
          fullNamePath={fullNamePath}
          readOnly={readOnly}
          disabled={disabled}
          required={required}
          removeAdditionalTariff={removeAdditionalTariff}
        />
      )}
      {(showMismatchAlert || showAIDrawer || showSuccessMessage) && (
        <div style={{ marginTop: '16px', marginBottom: '16px' }}>
          <AIDrawerAlertMessageBox
            showMismatchAlert={showMismatchAlert}
            showSuccessMessage={showSuccessMessage}
            fullNamePath={fullNamePath}
            onAccept={() => {
              setShowMismatchAlert(false);
              if (validationResult?.errorCode === 'NOT_ENOUGH_INFORMATION') {
                setShowSuccessMessage(true);
              }
            }}
            onClose={() => {
              setShowSuccessMessage(false);
            }}
            validationResult={validationResult}
          />
          {!showMismatchAlert && !showSuccessMessage && (
            <AIDrawer
              productDescription={productDescription}
              manufacturerName={manufacturerName}
              countryOfOrigin={countryOfOrigin}
              fullNamePath={fullNamePath}
              onAccept={() => {
                setShowAIDrawer(false);
                setShowMismatchAlert(false);
                if (validationResult?.errorCode === 'NOT_ENOUGH_INFORMATION') {
                  setShowSuccessMessage(true);
                }
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default memo(LineInfoTab);
